import * as React from "react";
import { Account } from "@/types";
import { cleanedErrors } from "@/utils/Functions";
import { BooleanInput } from "@/components/atoms/BooleanInput";
import { useOnChange } from "@/utils/callbacks";
import { TextInput } from "@/components/atoms/TextInput";
import { FormField } from "@/components/molecules/FormField";
import { FormStack } from "@/components/molecules/FormStack";
import { NestedErrors } from "@/utils/Types";

export type AccountNumberRecord = {
  readonly accountNumber?: string;
  readonly accountNumberUnknown?: boolean;
};

export type AccountNumberErrors = NestedErrors<AccountNumberRecord> | undefined;

export const accountNumberRecordFromAccount = (account?: Account): AccountNumberRecord => {
  if (!account) return {};

  return {
    accountNumber: account.accountNumber,
    accountNumberUnknown: !account.accountNumber,
  };
};

export const accountNumberAccountFieldsFromRecord = (
  record?: AccountNumberRecord
): Partial<Account> => {
  if (!record) return {};

  return {
    accountNumber: record.accountNumber ?? undefined,
  };
};

export const accountNumberErrorsOf: (r?: AccountNumberRecord) => AccountNumberErrors = (r) => {
  return cleanedErrors({
    accountNumber: r?.accountNumber ?? r?.accountNumberUnknown ? undefined : "required",
    accountNumberUnknown: r?.accountNumber ?? r?.accountNumberUnknown ? undefined : "required",
  });
};

export const AccountNumberField: React.FC<{
  readonly record: AccountNumberRecord;
  readonly update: (f: (record: AccountNumberRecord) => AccountNumberRecord) => void;
  readonly errors: AccountNumberErrors;
}> = ({ record, update, errors }) => {
  const empty = !record.accountNumber && !record.accountNumberUnknown;

  const onChange = useOnChange(update);

  const updateUnknown = React.useCallback(
    (unknown: boolean) => {
      update((s) => ({
        ...s,
        accountNumber: unknown ? undefined : "",
        accountNumberUnknown: unknown,
      }));
    },
    [update]
  );

  return (
    <FormStack substack spacing={0}>
      <FormField label={"Please provide the customer account number:"}>
        <TextInput
          name="accountNumber"
          value={record.accountNumber ?? ""}
          onChange={onChange}
          error={errors?.accountNumber && empty ? "required" : undefined}
          placeholder=""
          disabled={record.accountNumberUnknown}
        />
      </FormField>

      <FormField>
        <BooleanInput
          value={record.accountNumberUnknown}
          onValueChange={updateUnknown}
          label="I don't know the customer account number"
          disabled={!!record.accountNumber}
          error={!!errors?.accountNumberUnknown && empty ? "required" : undefined}
        />
      </FormField>
    </FormStack>
  );
};
